import React, { useState, useRef, useEffect, ReactNode } from 'react';

/**
 * Hook to detect touch capabilities of the device
 */
export const useTouchCapability = () => {
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  
  useEffect(() => {
    // Check if device supports touch events
    const hasTouchSupport = 'ontouchstart' in window || 
      navigator.maxTouchPoints > 0 ||
      (navigator as any).msMaxTouchPoints > 0;
    
    setIsTouchDevice(hasTouchSupport);
  }, []);
  
  return isTouchDevice;
};

/**
 * Hook for handling swipe gestures
 */
export const useSwipeGesture = (
  onSwipeLeft?: () => void,
  onSwipeRight?: () => void,
  onSwipeUp?: () => void,
  onSwipeDown?: () => void,
  threshold = 50
) => {
  const touchStart = useRef<{ x: number; y: number } | null>(null);
  const touchEnd = useRef<{ x: number; y: number } | null>(null);
  
  // The minimal distance required to detect a swipe
  const minSwipeDistance = threshold;
  
  const onTouchStart = (e: React.TouchEvent) => {
    touchEnd.current = null; // Reset touch end
    touchStart.current = {
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY
    };
  };
  
  const onTouchMove = (e: React.TouchEvent) => {
    touchEnd.current = {
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY
    };
  };
  
  const onTouchEnd = () => {
    if (!touchStart.current || !touchEnd.current) return;
    
    const distanceX = touchStart.current.x - touchEnd.current.x;
    const distanceY = touchStart.current.y - touchEnd.current.y;
    const isHorizontalSwipe = Math.abs(distanceX) > Math.abs(distanceY);
    
    // Detect horizontal swipes
    if (isHorizontalSwipe) {
      if (Math.abs(distanceX) > minSwipeDistance) {
        if (distanceX > 0 && onSwipeLeft) {
          onSwipeLeft();
        } else if (distanceX < 0 && onSwipeRight) {
          onSwipeRight();
        }
      }
    }
    // Detect vertical swipes
    else {
      if (Math.abs(distanceY) > minSwipeDistance) {
        if (distanceY > 0 && onSwipeUp) {
          onSwipeUp();
        } else if (distanceY < 0 && onSwipeDown) {
          onSwipeDown();
        }
      }
    }
    
    // Reset values
    touchStart.current = null;
    touchEnd.current = null;
  };
  
  return {
    onTouchStart,
    onTouchMove,
    onTouchEnd
  };
};

interface SwipeableContainerProps {
  children: ReactNode;
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  onSwipeUp?: () => void;
  onSwipeDown?: () => void;
  className?: string;
  threshold?: number;
  disableIfNotTouch?: boolean;
}

/**
 * A container that supports swipe gestures
 */
export const SwipeableContainer: React.FC<SwipeableContainerProps> = ({
  children,
  onSwipeLeft,
  onSwipeRight,
  onSwipeUp,
  onSwipeDown,
  className = '',
  threshold = 50,
  disableIfNotTouch = true
}) => {
  const isTouchDevice = useTouchCapability();
  
  const { onTouchStart, onTouchMove, onTouchEnd } = useSwipeGesture(
    onSwipeLeft,
    onSwipeRight,
    onSwipeUp,
    onSwipeDown,
    threshold
  );
  
  // If it's not a touch device and disableIfNotTouch is true, don't add touch handlers
  if (!isTouchDevice && disableIfNotTouch) {
    return <div className={className}>{children}</div>;
  }
  
  return (
    <div
      className={className}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {children}
    </div>
  );
};

interface PinchZoomProps {
  children: ReactNode;
  maxScale?: number;
  minScale?: number;
  className?: string;
}

/**
 * A container that supports pinch zoom gestures
 */
export const PinchZoomContainer: React.FC<PinchZoomProps> = ({
  children,
  maxScale = 3,
  minScale = 1,
  className = ''
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [initialDistance, setInitialDistance] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [translateX, setTranslateX] = useState(0);
  const [translateY, setTranslateY] = useState(0);
  const lastPosition = useRef({ x: 0, y: 0 });
  
  // Handle pinch zoom
  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      // Calculate initial distance between two touch points
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      );
      setInitialDistance(distance);
    } else if (e.touches.length === 1 && scale > 1) {
      // Start dragging when zoomed in
      setIsDragging(true);
      lastPosition.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }
  };
  
  const handleTouchMove = (e: React.TouchEvent) => {
    if (initialDistance !== null && e.touches.length === 2) {
      // Calculate new distance for the pinch gesture
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      );
      
      // Calculate the new scale
      const newScale = Math.min(
        maxScale,
        Math.max(minScale, scale * (distance / initialDistance))
      );
      
      setScale(newScale);
      setInitialDistance(distance);
      
      // Prevent default browser behavior (like zoom or scroll)
      e.preventDefault();
    } else if (isDragging && e.touches.length === 1) {
      // Handle dragging when zoomed in
      const touch = e.touches[0];
      const deltaX = touch.clientX - lastPosition.current.x;
      const deltaY = touch.clientY - lastPosition.current.y;
      
      setTranslateX(prev => prev + deltaX);
      setTranslateY(prev => prev + deltaY);
      
      lastPosition.current = { x: touch.clientX, y: touch.clientY };
      e.preventDefault();
    }
  };
  
  const handleTouchEnd = () => {
    setInitialDistance(null);
    setIsDragging(false);
    
    // If scale returns to 1, reset translation
    if (scale === 1) {
      setTranslateX(0);
      setTranslateY(0);
    }
  };
  
  // Double tap to zoom in/out
  const lastTap = useRef(0);
  const handleDoubleTap = (e: React.TouchEvent) => {
    const currentTime = new Date().getTime();
    const tapLength = currentTime - lastTap.current;
    
    if (tapLength < 300 && tapLength > 0) {
      // Double tap detected
      if (scale > 1) {
        // Reset zoom
        setScale(1);
        setTranslateX(0);
        setTranslateY(0);
      } else {
        // Zoom in centered on tap location
        setScale(2); // Use a moderate zoom level for double tap
        
        if (containerRef.current) {
          const rect = containerRef.current.getBoundingClientRect();
          const touch = e.touches[0] || e.changedTouches[0];
          
          // Calculate position relative to container
          const relativeX = touch.clientX - rect.left;
          const relativeY = touch.clientY - rect.top;
          
          // Center zoom on touch point
          const centerX = rect.width / 2;
          const centerY = rect.height / 2;
          
          setTranslateX((centerX - relativeX) * 0.5);
          setTranslateY((centerY - relativeY) * 0.5);
        }
      }
      
      e.preventDefault();
    }
    
    lastTap.current = currentTime;
  };
  
  return (
    <div
      ref={containerRef}
      className={`overflow-hidden ${className}`}
      onTouchStart={(e) => {
        handleTouchStart(e);
        handleDoubleTap(e);
      }}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{
        touchAction: scale > 1 ? 'none' : 'auto',
      }}
    >
      <div
        style={{
          transform: `scale(${scale}) translate(${translateX}px, ${translateY}px)`,
          transformOrigin: 'center',
          transition: isDragging ? 'none' : 'transform 0.1s ease-out',
          width: '100%',
          height: '100%',
        }}
      >
        {children}
      </div>
    </div>
  );
};

interface TouchFriendlyButtonProps {
  children: ReactNode;
  onClick: () => void;
  className?: string;
  disabled?: boolean;
  ariaLabel?: string;
  fullWidth?: boolean;
  variant?: 'primary' | 'secondary' | 'outline' | 'text';
  icon?: ReactNode;
  iconPosition?: 'left' | 'right';
}

/**
 * A button optimized for touch interactions with landscape orientation support
 * Features:
 * - Responsive sizing based on orientation
 * - Optimized touch targets (min 48px)
 * - Visual feedback for touch interactions
 * - Supports icons and different variants
 * - Enhanced for landscape mode usability
 */
export const TouchFriendlyButton: React.FC<TouchFriendlyButtonProps> = ({
  children,
  onClick,
  className = '',
  disabled = false,
  ariaLabel,
  fullWidth = false,
  variant = 'primary',
  icon,
  iconPosition = 'left'
}) => {
  const isTouchDevice = useTouchCapability();
  const { isPortrait, width } = useScreenSize();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [isTouched, setIsTouched] = useState(false);
  
  // For touch devices, add feedback styles
  const touchStart = () => {
    if (!disabled) {
      setIsTouched(true);
    }
  };
  
  const touchEnd = () => {
    setIsTouched(false);
  };
  
  // Enhanced target area for touch devices with orientation adjustments
  const touchFriendlyClass = isTouchDevice 
    ? isPortrait
      ? 'py-3 px-5 min-h-[48px] min-w-[48px]' // Portrait mode - taller
      : 'py-2 px-5 min-h-[48px] min-w-[56px]' // Landscape mode - wider
    : 'py-2 px-4';
  
  // Visual feedback for touch
  const touchStateClass = isTouched ? 'brightness-90 scale-95' : '';
  
  // Full width in portrait, or optionally in any orientation
  const widthClass = (isPortrait && width < 640) || fullWidth ? 'w-full' : '';
  
  // Button styling based on variant
  const variantClasses = {
    primary: 'bg-blue-600 hover:bg-blue-700 text-white',
    secondary: 'bg-gray-600 hover:bg-gray-700 text-white',
    outline: 'bg-transparent border border-blue-600 text-blue-600 hover:bg-blue-50',
    text: 'bg-transparent text-blue-600 hover:bg-blue-50 px-2'
  };
  
  // Landscape-specific adjustments
  const landscapeAdjustment = (!isPortrait && isTouchDevice) 
    ? 'text-base font-medium' // Slightly larger text in landscape
    : '';
  
  return (
    <button
      ref={buttonRef}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      className={`
        ${touchFriendlyClass}
        ${touchStateClass}
        ${widthClass}
        ${variantClasses[variant]}
        ${landscapeAdjustment}
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'transition-all duration-150'}
        rounded-lg flex items-center justify-center gap-2
        ${className}
      `}
      onTouchStart={touchStart}
      onTouchEnd={touchEnd}
      onTouchCancel={touchEnd}
    >
      {icon && iconPosition === 'left' && <span className="inline-flex">{icon}</span>}
      <span>{children}</span>
      {icon && iconPosition === 'right' && <span className="inline-flex">{icon}</span>}
    </button>
  );
};

interface CardSwipeListProps<T> {
  items: T[];
  renderItem: (item: T, index: number) => ReactNode;
  onSwipeLeft?: (item: T, index: number) => void;
  onSwipeRight?: (item: T, index: number) => void;
  className?: string;
  itemClassName?: string;
}

/**
 * A swipeable card list component for mobile reports
 */
export function CardSwipeList<T>({
  items,
  renderItem,
  onSwipeLeft,
  onSwipeRight,
  className = '',
  itemClassName = ''
}: CardSwipeListProps<T>) {
  // State to track active swipe indicators
  const [swipeStates, setSwipeStates] = useState<{ [key: number]: 'left' | 'right' | null }>({});
  
  const handleSwipeLeft = (index: number) => {
    // Show swipe indicator
    setSwipeStates(prev => ({ ...prev, [index]: 'left' }));
    
    // Call the handler after animation completes
    if (onSwipeLeft) {
      setTimeout(() => {
        onSwipeLeft(items[index], index);
        // Reset swipe state
        setSwipeStates(prev => ({ ...prev, [index]: null }));
      }, 300);
    }
  };
  
  const handleSwipeRight = (index: number) => {
    // Show swipe indicator
    setSwipeStates(prev => ({ ...prev, [index]: 'right' }));
    
    // Call the handler after animation completes
    if (onSwipeRight) {
      setTimeout(() => {
        onSwipeRight(items[index], index);
        // Reset swipe state
        setSwipeStates(prev => ({ ...prev, [index]: null }));
      }, 300);
    }
  };
  
  return (
    <div className={`flex flex-col gap-4 ${className}`}>
      {items.map((item, index) => {
        const swipeState = swipeStates[index];
        
        // Apply transition class based on swipe direction
        const swipeClass = swipeState === 'left'
          ? 'translate-x-[-100%] opacity-0'
          : swipeState === 'right'
            ? 'translate-x-[100%] opacity-0'
            : '';
        
        return (
          <div 
            key={index}
            className={`
              ${swipeClass} 
              transition-transform duration-300 ease-out
            `}
          >
            <SwipeableContainer
              onSwipeLeft={() => handleSwipeLeft(index)}
              onSwipeRight={() => handleSwipeRight(index)}
              className={`
                bg-white rounded-lg shadow-md overflow-hidden
                ${itemClassName}
              `}
            >
              {renderItem(item, index)}
            </SwipeableContainer>
          </div>
        );
      })}
    </div>
  );
}

/**
 * A hook for detecting screen size and orientation
 */
export const useScreenSize = () => {
  const [screenSize, setScreenSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
    isMobile: window.innerWidth < 768,
    isPortrait: window.innerHeight > window.innerWidth
  });
  
  useEffect(() => {
    const handleResize = () => {
      setScreenSize({
        width: window.innerWidth,
        height: window.innerHeight,
        isMobile: window.innerWidth < 768,
        isPortrait: window.innerHeight > window.innerWidth
      });
    };
    
    // Listen for window resize events
    window.addEventListener('resize', handleResize);
    
    // Listen for orientation change on mobile
    window.addEventListener('orientationchange', handleResize);
    
    // Clean up
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);
  
  return screenSize;
};

export default {
  SwipeableContainer,
  PinchZoomContainer,
  TouchFriendlyButton,
  CardSwipeList,
  useTouchCapability,
  useSwipeGesture,
  useScreenSize
};